/*
  dopamiles-collection.js
  Phase 06 extraction (260511) — was inline in sections/dopamiles-collection-grid.liquid.

  Two responsibilities:
    1. Filter drawer open/close (#doc-open-filters → #doc-drawer + bg overlay).
       ESC closes the drawer. Body scroll locks while open.
    2. Sort dropdown navigation. Replaces the prior inline `onchange` regex hack
       with URLSearchParams so existing facet params (filter.p.*, filter.v.*)
       are preserved when the user changes sort order.

  Hooks (set by sections/dopamiles-collection-grid.liquid):
    [data-bnc-sort]               — sort <select> element
    [data-bnc-collection-url]     — base collection URL (set on sort element)
    #doc-open-filters              — drawer trigger button
    #doc-close-drawer              — drawer close button
    #doc-drawer-bg                 — backdrop (click to close)
    #doc-drawer                    — drawer panel
*/
(function () {
  'use strict';

  // Drawer-close callback shared between init and the document-scope keydown
  // listener (registered ONCE — review D1: previous version leaked one
  // listener per shopify:section:load).
  var currentClose = null;

  function initDrawer() {
    var bg     = document.getElementById('doc-drawer-bg');
    var dr     = document.getElementById('doc-drawer');
    var openB  = document.getElementById('doc-open-filters');
    var closeB = document.getElementById('doc-close-drawer');
    if (!dr) { currentClose = null; return; }

    function open() {
      if (bg)    bg.classList.add('open');
      dr.classList.add('open');
      dr.setAttribute('aria-hidden', 'false');
      if (openB) openB.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      if (bg)    bg.classList.remove('open');
      dr.classList.remove('open');
      dr.setAttribute('aria-hidden', 'true');
      if (openB) openB.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    // Re-bind click handlers each boot (drawer DOM was replaced).
    if (openB)  openB.addEventListener('click', open);
    if (closeB) closeB.addEventListener('click', close);
    if (bg)     bg.addEventListener('click', close);

    currentClose = close;
  }

  // Single document-scope ESC listener for the whole page lifetime.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && currentClose) currentClose();
  });

  function initSort() {
    var sel = document.querySelector('[data-bnc-sort]');
    if (!sel) return;

    sel.addEventListener('change', function () {
      var baseUrl = sel.getAttribute('data-bnc-collection-url') || window.location.pathname;
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', sel.value);
      // Preserve all existing facet params; only sort_by is replaced.
      window.location = baseUrl + '?' + params.toString();
    });
  }

  // Phase 07: quick-add button (rendered by snippets/dopamiles-product-card.liquid
  // when `show_quick_add` is true). Event delegation on document so re-rendered
  // grids (section reload, future infinite-scroll) keep working.
  //
  // Flow: click → POST cart_add_url with first-available variant ID →
  //       refresh drawer via window.bncCart → open drawer →
  //       on error (e.g. 422 sold-out), show visible toast (plan risk).
  //
  // Concurrency: per-button `disabled` only (review H1). Different cards
  // can be tapped in parallel; same card is debounced by btn.disabled.
  function showQuickAddToast(text) {
    var existing = document.querySelector('.bnc-quick-add-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.className = 'bnc-quick-add-toast';
    t.textContent = text;
    t.setAttribute('role', 'status');
    t.style.cssText = [
      'position:fixed', 'bottom:80px', 'left:50%', 'transform:translateX(-50%)',
      'background:var(--bnc-ink, #3B352C)', 'color:#fff',
      'padding:12px 20px', 'border-radius:999px',
      'z-index:1000', 'box-shadow:0 4px 16px rgba(0,0,0,.2)',
      'font-size:14px', 'font-weight:500', 'max-width:80vw',
      'text-align:center'
    ].join(';');
    document.body.appendChild(t);
    setTimeout(function () { if (t.parentNode) t.remove(); }, 3500);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bnc-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    if (addUrl.indexOf('.js') === -1) addUrl += '.js';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function () {
        if (window.bncCart && typeof window.bncCart.refresh === 'function') {
          return window.bncCart.refresh().then(function () {
            if (typeof window.bncCart.open === 'function') window.bncCart.open();
          });
        }
      })
      .catch(function (err) {
        // 422 = sold-out / variant unavailable. Surface Shopify's description if given.
        var msg = (err && (err.description || err.message)) || 'Could not add to cart';
        if (window.console) console.warn('[bnc-quick-add]', msg, err);
        showQuickAddToast(msg);
      })
      .finally(function () {
        btn.disabled = false;
      });
  });

  function boot() {
    initDrawer();
    initSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor: re-init when the collection section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-bnc-sort], #doc-drawer')) {
      boot();
    }
  });
})();
